import { lookup } from "mime-types";

/**
 * Supported file types for order attachments.
 * This is the single source of truth used by:
 * - The file input `accept` attribute (browser hint)
 * - The runtime file type validation (rejects invalid files)
 *
 * Must stay in sync with:
 * - API Gateway: x-amazon-apigateway-binary-media-types (openapi-frontend.json)
 * - Lambda proxy: ALLOWED_BINARY_CONTENT_TYPES (taxonomy-proxy-lib/base-proxy.js)
 */
export const ALLOWED_ATTACHMENT_TYPES: string[] = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/octet-stream",
    "application/vnd.ms-excel",
    "application/vnd.ms-outlook",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/zip",
    "image/jpeg",
    "image/png",
    "message/rfc822",
    "text/csv",
    "text/plain",
];

/**
 * Allowed file extensions for fallback validation when MIME type is unavailable.
 * Maps to the MIME types above.
 */
const ALLOWED_EXTENSIONS: string[] = [
    ".pdf",
    ".doc",
    ".docx",
    ".bin",
    ".xls",
    ".xlsx",
    ".msg",
    ".eml",
    ".zip",
    ".jpg",
    ".jpeg",
    ".png",
    ".csv",
    ".txt",
];

/**
 * Human-readable list of accepted file formats for display in error messages.
 */
export const ACCEPTED_FORMATS_DISPLAY = "PDF, Word (.doc, .docx), Excel, Email (MSG, EML), images (JPEG, PNG), ZIP, CSV, TXT";

/**
 * Maximum file size allowed per attachment (4MB in bytes).
 */
export const MAX_FILE_SIZE = 4 * 1024 * 1024;

/**
 * Value for the HTML file input `accept` attribute.
 * Includes both MIME types and file extensions to ensure cross-platform compatibility.
 * macOS doesn't recognize some MIME types (e.g., application/vnd.ms-outlook for .msg)
 * so file extensions are needed as a fallback for the OS file picker.
 */
export const FILE_INPUT_ACCEPT = [...ALLOWED_ATTACHMENT_TYPES, ...ALLOWED_EXTENSIONS].join(",");

/**
 * Extracts the file extension from a filename (lowercase, with dot).
 *
 * @param fileName - The file name to extract the extension from
 * @returns The extension including the dot (e.g., ".pdf") or empty string if none
 */
const getFileExtension = (fileName: string): string => {
    const lastDot = fileName.lastIndexOf(".");
    if (lastDot === -1) return "";
    return fileName.substring(lastDot).toLowerCase();
};

/**
 * Validates whether a file has an accepted type.
 * Checks the MIME type first; if the browser didn't detect one (empty string),
 * falls back to file extension validation via mime-types lookup.
 *
 * @param file - The File object to validate
 * @returns true if the file type is allowed, false otherwise
 */
export const isFileTypeAllowed = (file: File): boolean => {
    const mimeType = file.type;

    if (mimeType) {
        return ALLOWED_ATTACHMENT_TYPES.includes(mimeType);
    }

    // Fallback: browser didn't detect MIME type — validate by extension
    const extension = getFileExtension(file.name);
    if (!extension) return false;

    if (ALLOWED_EXTENSIONS.includes(extension)) return true;

    // Last resort: use mime-types library to resolve extension to MIME
    const resolvedMime = lookup(file.name);
    if (resolvedMime) {
        return ALLOWED_ATTACHMENT_TYPES.includes(resolvedMime);
    }

    return false;
};

/**
 * Validates whether a file is within the allowed size limit.
 *
 * @param file - The File object to validate
 * @returns true if the file size is within the limit, false otherwise
 */
export const isFileSizeAllowed = (file: File): boolean => {
    return file.size <= MAX_FILE_SIZE;
};

/**
 * Filters files into accepted, type-rejected, and size-rejected groups.
 *
 * @param files - Array of File objects to validate
 * @returns Object containing arrays of accepted, type-rejected, and size-rejected files
 */
export const validateFiles = (files: File[]): {
    accepted: File[];
    rejectedByType: File[];
    rejectedBySize: File[];
} => {
    const accepted: File[] = [];
    const rejectedByType: File[] = [];
    const rejectedBySize: File[] = [];

    for (const file of files) {
        if (!isFileTypeAllowed(file)) {
            rejectedByType.push(file);
        } else if (!isFileSizeAllowed(file)) {
            rejectedBySize.push(file);
        } else {
            accepted.push(file);
        }
    }

    return { accepted, rejectedByType, rejectedBySize };
};
